import {
  Box,
  HStack,
  Link as ChakraLink,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Portal,
  Spacer,
  Spinner,
} from '@chakra-ui/react';
import { BackofficeOrderDetail } from '@diamond/shared/types';
import { Button as UIButton, Icon, Text, TextField } from '@diamond/shared/ui';
import { statusFormatter } from '@diamond/shared/utils';
import { Menu as MenuIcon } from '@mui/icons-material';
import { useCallback } from 'react';

import { useActivitiesDetail } from '../../api';
import { formatCurrency } from '../../utils/activity-formatters';
import { useActivityAction } from './action-context';
import { CancelButton } from './cancel-button';
import { SubmitButton } from './submit-button';

type Props = {
  activitiesId: string;
  // Keep cache alignment with page query
  queryString: {
    order_by: string;
    direction: string;
    size: number;
    page: number;
  };
  cancelReason: string;
  onOpenOrderHistory: () => void;
  // react-hook-form register for the read-only total field
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  register: any;
  refetch: () => Promise<unknown> | unknown;
};

export function ActivityDetailActionBar({
  activitiesId,
  queryString,
  cancelReason,
  onOpenOrderHistory,
  register,
  refetch,
}: Props) {
  const selectHeader = useCallback(
    (d: BackofficeOrderDetail) => ({
      status: d.order.status,
      count_items: d.order.count_items,
      sap_note: d.order.sap_note,
      invoice_link: d.order.invoice_link,
      delivery_link: d.order.delivery_link,
      total: d.total,
    }),
    []
  );
  const { activitiesDetailData: header } = useActivitiesDetail(
    activitiesId,
    queryString,
    { select: selectHeader }
  );

  const statusData = header?.status;
  const { submitBusy, cancelBusy } = useActivityAction();
  const isMenuDisabled =
    !header ||
    submitBusy ||
    cancelBusy ||
    statusData === 'waiting_for_payment' ||
    statusData === 'submitted';

  return (
    <HStack spacing="4" alignItems="center" w="full">
      <Box px="4">
        <Text fontSize="md" color="blue">
          {header?.count_items} Produk
        </Text>
      </Box>
      <Spacer />
      <Box py="2" px="4" borderRadius="lg" bgColor="#0000a31a" color="blue">
        <Text>
          Status{' '}
          {statusFormatter(statusData, cancelReason, header?.sap_note || '')}
        </Text>
      </Box>
      <Box>
        <Menu>
          <MenuButton
            as={UIButton}
            aria-label="menu"
            variant="ghost"
            name="submit-button"
            color="blue"
            bgColor="#0000a31a"
            pt={1}
            px={0}
          >
            <Icon as={MenuIcon} />
          </MenuButton>
          <Portal>
            <MenuList>
              <MenuItem
                as={ChakraLink}
                href={header?.delivery_link}
                isDisabled={isMenuDisabled}
                isExternal
              >
                Delivery Info
              </MenuItem>
              <MenuItem
                as={ChakraLink}
                href={header?.invoice_link}
                isDisabled={isMenuDisabled}
                isExternal
              >
                Invoice
              </MenuItem>
              <MenuItem onClick={onOpenOrderHistory}>Riwayat Pesanan</MenuItem>
            </MenuList>
          </Portal>
        </Menu>
      </Box>
      <Box>
        <TextField
          name="total"
          placeholder={'Total Nilai Order'}
          register={register}
          value={`Total: ${formatCurrency(header?.total as number)}`}
          isDisabled
        />
      </Box>
      <Box display="flex" alignItems="center">
        {submitBusy || cancelBusy ? (
          <HStack mr={2} spacing={2}>
            <Spinner size="sm" />
            <Text fontSize="sm">Memproses…</Text>
          </HStack>
        ) : null}
        {!header ? (
          <HStack mr={2} spacing={2}>
            <Spinner size="sm" />
            <Text fontSize="sm">Memuat…</Text>
          </HStack>
        ) : null}
        <SubmitButton
          activitiesId={activitiesId}
          refetch={refetch}
          queryString={queryString}
        />
        <CancelButton activitiesId={activitiesId} queryString={queryString} />
      </Box>
    </HStack>
  );
}

export default ActivityDetailActionBar;
